import { EntryEditError } from '../edits.ts';
import { javaCompare } from '../exportData.ts';
import { parseJavaProperties } from '../javaProperties.ts';
import { javaStrip } from '../javaText.ts';
import { ENTITY_TYPE_NAMES } from '../model.ts';
import { text, int, type SqlConnection, type SqlRow } from '../sql.ts';
import { activeCalendar } from '../calendar/calendars.ts';
import { CalendarError, daysPerYear, fromAbsoluteDay, type CalendarSystem } from '../calendar/calendarEngine.ts';
import { displayDate, type ChronologyDate } from '../calendar/chronologyDate.ts';
import { DATE_FACTS, QUANTITY_FACTS, count, dateOf, normalizeKey, parseYear, quantityOf, unreadable, years, type DateFact, type Dated, type Fields } from './worldFacts.ts';

/**
 * The Consistency Check (docs/PARITY-STEP-5-PLAN.md section 6), ported from the desktop's
 * `consistency` package rule for rule (decision 6): a snapshot of the realm (its entries
 * with their fields, the relationships between them, the Chronology's events and eras,
 * which entries the Atlas places, and the active calendar), every rule run over it, and
 * the findings most serious first. Dismissals are kept in `consistency_dismissals` by
 * the finding's fingerprint, as the desktop keeps them. schema/fixtures/consistency-cases.json,
 * written by the desktop, keeps the two in step.
 */

export type Severity = 'ERROR' | 'WARNING' | 'INFO';
export const SEVERITIES: readonly Severity[] = ['ERROR', 'WARNING', 'INFO'];
export const SEVERITY_LABELS: Readonly<Record<Severity, string>> = { ERROR: 'Contradiction', WARNING: 'Needs review', INFO: 'Suggestion' };
export const SEVERITY_GLYPHS: Readonly<Record<Severity, string>> = { ERROR: '⛔', WARNING: '⚠', INFO: '💡' };

export interface Finding {
  readonly ruleId: string;
  readonly severity: Severity;
  readonly title: string;
  readonly message: string;
  /** The entries involved, in the order the message names them. */
  readonly entryIds: readonly string[];
  /** What makes it distinct within its rule; with the rule, its fingerprint. */
  readonly key: string;
}

/** What a dismissal is remembered by: rewording a message never brings a finding back. */
export const fingerprint = (finding: Finding) => `${finding.ruleId}|${finding.key}`;

export interface WorldEntry {
  readonly id: string;
  readonly entityType: string;
  readonly customTypeName: string;
  readonly title: string;
  /** In the entry's order: by name, as the desktop reads stored fields. */
  readonly fields: Fields;
}

export interface WorldLink {
  readonly sourceEntryId: string;
  readonly targetEntryId: string;
  readonly relationshipType: string;
}

export interface WorldEvent {
  readonly id: string;
  readonly eraId: string | null;
  readonly title: string;
  readonly start: ChronologyDate;
  readonly end: ChronologyDate | null;
  readonly entryIds: readonly string[];
}

export interface WorldEra {
  readonly id: string;
  readonly name: string;
  readonly start: ChronologyDate;
  readonly end: ChronologyDate | null;
}

export interface World {
  readonly entries: readonly WorldEntry[];
  readonly links: readonly WorldLink[];
  readonly events: readonly WorldEvent[];
  readonly eras: readonly WorldEra[];
  readonly entryIdsOnMaps: ReadonlySet<string>;
  readonly calendar: CalendarSystem | null;
}

export interface ConsistencyRule {
  readonly id: string;
  readonly name: string;
  readonly description: string;
}

interface Rule extends ConsistencyRule {
  check(world: WorldView): Finding[];
}

// ---- the world, as rules read it (WorldModel) ----

class WorldView {
  readonly #entries: Map<string, WorldEntry>;
  readonly #eras: Map<string, WorldEra>;

  constructor(readonly world: World) {
    this.#entries = new Map();
    for (const entry of world.entries) if (!this.#entries.has(entry.id)) this.#entries.set(entry.id, entry);
    this.#eras = new Map();
    for (const era of world.eras) if (!this.#eras.has(era.id)) this.#eras.set(era.id, era);
  }

  get entries() {
    return this.world.entries;
  }
  get links() {
    return this.world.links;
  }
  get events() {
    return this.world.events;
  }

  entry(id: string): WorldEntry | undefined {
    return this.#entries.get(id);
  }

  era(id: string | null): WorldEra | undefined {
    return id === null ? undefined : this.#eras.get(id);
  }

  /** An entry's title for a message, or a placeholder for an id with no entry behind it. */
  title(id: string): string {
    return this.#entries.get(id)?.title ?? '(missing entry)';
  }

  /** The year a Chronology date falls in: through the active calendar, else read from its label. */
  year(date: ChronologyDate): number | null {
    const calendar = this.world.calendar;
    if (calendar && daysPerYear(calendar) > 0) {
      const perYear = daysPerYear(calendar);
      const calendarDate = fromAbsoluteDay(calendar, Number(date.sortKey));
      const dayOfYear = Number(((date.sortKey % BigInt(perYear)) + BigInt(perYear)) % BigInt(perYear));
      return calendarDate.year + dayOfYear / perYear;
    }
    return parseYear(date.label);
  }
}

/** `LoreEntry.displayTypeName`: a custom type's own name, or the type's. */
export function typeName(entry: Pick<WorldEntry, 'entityType' | 'customTypeName'>): string {
  return entry.entityType === 'CUSTOM' && entry.customTypeName.trim() !== '' ? entry.customTypeName : (ENTITY_TYPE_NAMES[entry.entityType] ?? entry.entityType);
}

// ---- relationship vocabulary (RelationshipVocabulary) ----

const SYMMETRIC = new Set(['allied with', 'ally of', 'enemy of', 'at war with', 'married to', 'spouse of', 'sibling of', 'brother of', 'sister of', 'friend of', 'friends with', 'rival of', 'trading partner of', 'sworn enemy of', 'hostile to']);
const FRIENDLY = new Set(['allied with', 'ally of', 'friend of', 'friends with', 'married to', 'spouse of', 'trading partner of']);
const HOSTILE = new Set(['enemy of', 'at war with', 'sworn enemy of', 'hostile to']);

type Hierarchy = 'ANCESTRY' | 'CONTAINMENT' | 'COMMAND';
const HIERARCHIES: readonly Hierarchy[] = ['ANCESTRY', 'CONTAINMENT', 'COMMAND'];
const HIERARCHY_TEXT: Record<Hierarchy, { label: string; phrase: string }> = {
  ANCESTRY: { label: 'ancestry', phrase: 'is a descendant of' },
  CONTAINMENT: { label: 'location', phrase: 'lies within' },
  COMMAND: { label: 'chain of command', phrase: 'answers to' },
};

const UPWARD: Record<string, Hierarchy> = {
  'child of': 'ANCESTRY', 'son of': 'ANCESTRY', 'daughter of': 'ANCESTRY', 'offspring of': 'ANCESTRY', 'heir of': 'ANCESTRY',
  'located in': 'CONTAINMENT', 'part of': 'CONTAINMENT', within: 'CONTAINMENT', inside: 'CONTAINMENT', 'region of': 'CONTAINMENT', 'province of': 'CONTAINMENT',
  'reports to': 'COMMAND', serves: 'COMMAND', 'vassal of': 'COMMAND', 'subordinate of': 'COMMAND', 'answers to': 'COMMAND',
};
const DOWNWARD: Record<string, Hierarchy> = {
  'parent of': 'ANCESTRY', 'father of': 'ANCESTRY', 'mother of': 'ANCESTRY',
  contains: 'CONTAINMENT', encompasses: 'CONTAINMENT',
  commands: 'COMMAND', 'superior of': 'COMMAND', 'liege of': 'COMMAND',
};

const ANY: ReadonlySet<string> = new Set();
const KIN = new Set(['PERSON', 'CREATURE', 'SPECIES', 'CUSTOM']);
const EXPECTED_TYPES: Record<string, readonly [ReadonlySet<string>, ReadonlySet<string>]> = {
  'child of': [KIN, KIN],
  'son of': [KIN, KIN],
  'daughter of': [KIN, KIN],
  'parent of': [KIN, KIN],
  'father of': [KIN, KIN],
  'mother of': [KIN, KIN],
  'married to': [KIN, KIN],
  'sibling of': [KIN, KIN],
  'located in': [ANY, new Set(['PLACE', 'CUSTOM'])],
  'member of': [ANY, new Set(['FACTION', 'RELIGION', 'CULTURE', 'SPECIES', 'CUSTOM'])],
  'ruler of': [new Set(['PERSON', 'FACTION', 'CREATURE', 'CUSTOM']), new Set(['PLACE', 'FACTION', 'CULTURE', 'RELIGION', 'CUSTOM'])],
  'founded by': [ANY, new Set(['PERSON', 'FACTION', 'CREATURE', 'RELIGION', 'CULTURE', 'CUSTOM'])],
  'reports to': [KIN, new Set(['PERSON', 'CREATURE', 'FACTION', 'CUSTOM'])],
};

const normalize = (type: string) => javaStrip(type).toLowerCase();
const has = (o: object, key: string) => Object.prototype.hasOwnProperty.call(o, key);

interface Step {
  readonly lower: string;
  readonly higher: string;
  readonly hierarchy: Hierarchy;
}

/** The link as a lower-to-higher hierarchy step, if its type belongs to a hierarchy. */
function step(link: WorldLink): Step | null {
  const type = normalize(link.relationshipType);
  if (has(UPWARD, type)) return { lower: link.sourceEntryId, higher: link.targetEntryId, hierarchy: UPWARD[type]! };
  if (has(DOWNWARD, type)) return { lower: link.targetEntryId, higher: link.sourceEntryId, hierarchy: DOWNWARD[type]! };
  return null;
}

const NOUN_TYPES = new Set([
  'enemy of', 'ally of', 'friend of', 'rival of', 'trading partner of', 'spouse of', 'sibling of', 'sworn enemy of', 'parent of', 'child of', 'member of',
  'vassal of', 'subordinate of', 'heir of', 'ruler of', 'province of', 'region of', 'part of', 'father of', 'mother of', 'son of', 'daughter of',
  'brother of', 'sister of', 'offspring of', 'liege of', 'superior of',
]);
const BARE_LEADS = new Set(['at', 'hostile', 'friends', 'within', 'inside', 'sworn']);

/** A relationship type as it reads after "is recorded as" (`RelationshipRules.phrase`). */
function phrase(type: string): string {
  const normalized = normalize(type);
  if (normalized === '') return '""';
  if (NOUN_TYPES.has(normalized)) return `${'aeiou'.includes(normalized[0]!) ? 'an ' : 'a '}${normalized}`;
  const lead = normalized.split(' ')[0]!;
  if (lead.endsWith('ed') || BARE_LEADS.has(lead)) return normalized;
  return `"${normalized}"`;
}

const pairKey = (a: string, b: string) => (javaCompare(a, b) < 0 ? `${a}+${b}` : `${b}+${a}`);
const sortedIds = (ids: Iterable<string>) => [...ids].sort(javaCompare);

// ---- relationship rules ----

const conflicts: Rule = {
  id: 'relationship.conflict',
  name: 'Conflicting relationships',
  description: 'Two entries recorded as both friendly (allied with, friend of, married to...) and hostile (enemy of, at war with...) toward each other, in either direction.',
  check(world) {
    const byPair = new Map<string, WorldLink[]>();
    for (const link of world.links) {
      const type = normalize(link.relationshipType);
      if (link.sourceEntryId !== link.targetEntryId && (FRIENDLY.has(type) || HOSTILE.has(type))) {
        const key = pairKey(link.sourceEntryId, link.targetEntryId);
        byPair.set(key, [...(byPair.get(key) ?? []), link]);
      }
    }
    const findings: Finding[] = [];
    for (const [pair, links] of byPair) {
      const a = links.find((l) => FRIENDLY.has(normalize(l.relationshipType)));
      const b = links.find((l) => HOSTILE.has(normalize(l.relationshipType)));
      if (!a || !b) continue;
      const message =
        `Conflicting relationship definitions: ${world.title(a.sourceEntryId)} is recorded as ${phrase(a.relationshipType)} ${world.title(a.targetEntryId)}, ` +
        `but ${world.title(b.sourceEntryId)} is recorded as ${phrase(b.relationshipType)} ${world.title(b.targetEntryId)}. ` +
        'Keep whichever is true now (or record the change as a chronology event).';
      findings.push({ ruleId: this.id, severity: 'ERROR', title: this.name, message, entryIds: [a.sourceEntryId, a.targetEntryId], key: pair });
    }
    return findings;
  },
};

const oneSided: Rule = {
  id: 'relationship.one-sided',
  name: 'One-sided relationship',
  description: "A relationship that's normally mutual (allied with, enemy of, married to, sibling of...) recorded from only one side.",
  check(world) {
    const recorded = new Set(world.links.map((l) => `${l.sourceEntryId}|${normalize(l.relationshipType)}|${l.targetEntryId}`));
    const conflicting = new Set(conflicts.check(world).map((f) => f.key));
    const findings: Finding[] = [];
    for (const link of world.links) {
      const type = normalize(link.relationshipType);
      if (
        !SYMMETRIC.has(type) ||
        link.sourceEntryId === link.targetEntryId ||
        !world.entry(link.targetEntryId) ||
        conflicting.has(pairKey(link.sourceEntryId, link.targetEntryId)) ||
        recorded.has(`${link.targetEntryId}|${type}|${link.sourceEntryId}`)
      ) {
        continue;
      }
      const message =
        `${world.title(link.sourceEntryId)} is recorded as ${phrase(type)} ${world.title(link.targetEntryId)}, but ${world.title(link.targetEntryId)} ` +
        'has no matching link back. That relationship usually goes both ways - add the reverse link, or dismiss this if the feeling really isn\'t mutual.';
      findings.push({ ruleId: this.id, severity: 'INFO', title: this.name, message, entryIds: [link.sourceEntryId, link.targetEntryId], key: `${link.sourceEntryId}|${type}|${link.targetEntryId}` });
    }
    return findings;
  },
};

const cycles: Rule = {
  id: 'relationship.cycle',
  name: 'Circular hierarchy',
  description: 'Ancestry (child of / parent of), location (located in / part of / contains), or chain of command (reports to / serves / commands) links that loop back to where they started.',
  check(world) {
    const findings: Finding[] = [];
    for (const hierarchy of HIERARCHIES) {
      const up = new Map<string, Set<string>>();
      for (const link of world.links) {
        const s = step(link);
        if (!s || s.hierarchy !== hierarchy || s.lower === s.higher) continue;
        let set = up.get(s.lower);
        if (!set) {
          set = new Set();
          up.set(s.lower, set);
        }
        set.add(s.higher);
      }
      const reported = new Set<string>();
      for (const cycle of findCycles(up)) {
        const key = `${hierarchy}:${[...new Set(sortedIds(cycle))].join('+')}`;
        if (reported.has(key)) continue;
        reported.add(key);
        let chain = '';
        for (let i = 0; i < cycle.length; i++) chain += `${world.title(cycle[i]!)}${i === 0 ? ' ' : ', which '}${HIERARCHY_TEXT[hierarchy].phrase} `;
        chain += world.title(cycle[0]!);
        const message = `Circular ${HIERARCHY_TEXT[hierarchy].label}: ${chain}. Something in that loop has to be the top.`;
        findings.push({ ruleId: this.id, severity: 'ERROR', title: this.name, message, entryIds: cycle, key });
      }
    }
    return findings;
  },
};

/** Every elementary cycle reachable by a depth-first walk, each starting at its first-visited member. */
function findCycles(graph: ReadonlyMap<string, ReadonlySet<string>>): string[][] {
  const found: string[][] = [];
  const finished = new Set<string>();
  const dfs = (node: string, path: string[], onPath: Set<string>) => {
    path.push(node);
    onPath.add(node);
    for (const next of graph.get(node) ?? []) {
      if (onPath.has(next)) found.push(path.slice(path.indexOf(next)));
      else if (!finished.has(next)) dfs(next, path, onPath);
    }
    onPath.delete(node);
    path.pop();
    finished.add(node);
  };
  for (const start of graph.keys()) if (!finished.has(start)) dfs(start, [], new Set());
  return found;
}

const selfLinks: Rule = {
  id: 'relationship.self',
  name: 'Entry related to itself',
  description: 'A relationship whose source and target are the same entry.',
  check(world) {
    return world.links
      .filter((l) => l.sourceEntryId === l.targetEntryId)
      .map((l) => ({
        ruleId: this.id,
        severity: 'WARNING' as const,
        title: this.name,
        message: `${world.title(l.sourceEntryId)} is recorded as ${phrase(l.relationshipType)} itself. That link probably meant to point at a different entry.`,
        entryIds: [l.sourceEntryId],
        key: `${l.sourceEntryId}|${normalize(l.relationshipType)}`,
      }));
  },
};

/** `UnusualTypes.describe`: "a place", or "faction, place or religion entries". */
function describeTypes(types: ReadonlySet<string>): string {
  const names = [...types].filter((t) => t !== 'CUSTOM').map((t) => t.toLowerCase()).sort(javaCompare);
  if (names.length === 1) return `a ${names[0]}`;
  return `${names.slice(0, -1).join(', ')} or ${names.at(-1)} entries`;
}

const unusualTypes: Rule = {
  id: 'relationship.unusual-type',
  name: 'Unusual relationship',
  description: 'A well-known relationship between kinds of entry it doesn\'t usually connect - e.g. "located in" pointing at a person, or "child of" between places.',
  check(world) {
    const findings: Finding[] = [];
    for (const link of world.links) {
      const type = normalize(link.relationshipType);
      const expected = has(EXPECTED_TYPES, type) ? EXPECTED_TYPES[type]! : null;
      const source = world.entry(link.sourceEntryId);
      const target = world.entry(link.targetEntryId);
      if (!expected || !source || !target) continue;
      const [subjects, targets] = expected;
      const badSubject = subjects.size > 0 && !subjects.has(source.entityType);
      const badTarget = targets.size > 0 && !targets.has(target.entityType);
      if (!badSubject && !badTarget) continue;
      const usual = badTarget ? `"${type}" usually points at ${describeTypes(targets)}` : `"${type}" usually starts from ${describeTypes(subjects)}`;
      const message = `${source.title} (${typeName(source)}) is recorded as ${phrase(type)} ${target.title} (${typeName(target)}), but ${usual}. Check the relationship type, or the entries' types.`;
      findings.push({ ruleId: this.id, severity: 'WARNING', title: this.name, message, entryIds: [link.sourceEntryId, link.targetEntryId], key: `${link.sourceEntryId}|${type}|${link.targetEntryId}` });
    }
    return findings;
  },
};

const tooManyParents: Rule = {
  id: 'family.too-many-parents',
  name: 'More than two parents',
  description: 'An entry with more than two recorded parents (child of / parent of). Fine for some species; often a mislabeled step-parent, guardian, or grandparent.',
  check(world) {
    const parents = new Map<string, Set<string>>();
    for (const link of world.links) {
      const s = step(link);
      if (!s || s.hierarchy !== 'ANCESTRY' || !world.entry(s.higher)) continue;
      parents.set(s.lower, (parents.get(s.lower) ?? new Set()).add(s.higher));
    }
    const findings: Finding[] = [];
    for (const [child, set] of parents) {
      if (set.size <= 2) continue;
      const ids = sortedIds(set);
      const names = ids.map((id) => world.title(id)).sort(javaCompare).join(', ');
      findings.push({
        ruleId: this.id,
        severity: 'INFO',
        title: this.name,
        message: `${world.title(child)} has ${set.size} recorded parents (${names}). If one is really a step-parent, guardian, or grandparent, a more specific relationship type will read better.`,
        entryIds: [child, ...ids],
        key: child,
      });
    }
    return findings;
  },
};

// ---- dates and numbers ----

const YOUNGEST_PLAUSIBLE_PARENT = 12;
const FOOD_SHORTFALL_RATIO = 0.8;
const BEINGS = new Set(['PERSON', 'CREATURE']);
const when = (fact: DateFact, dated: Dated) => `${DATE_FACTS[fact].verb} ${dated.display}`;

const startAfterEnd: Rule = {
  id: 'dates.start-after-end',
  name: 'Ends before it begins',
  description: 'An entry whose recorded death comes before its birth, or whose end (destroyed, fell, dissolved...) comes before its founding.',
  check(world) {
    const findings: Finding[] = [];
    const compare = (entry: WorldEntry, start: DateFact, end: DateFact) => {
      const from = dateOf(entry.fields, start);
      const to = dateOf(entry.fields, end);
      if (from && to && to.year < from.year) {
        findings.push({
          ruleId: this.id,
          severity: 'ERROR',
          title: this.name,
          message: `${entry.title}'s recorded ${DATE_FACTS[end].noun} (${when(end, to)}) comes ${years(from.year - to.year)} before its recorded ${DATE_FACTS[start].noun} (${when(start, from)}).`,
          entryIds: [entry.id],
          key: `${entry.id}|${start}`,
        });
      }
    };
    for (const entry of world.entries) {
      compare(entry, 'BIRTH', 'DEATH');
      compare(entry, 'FOUNDED', 'ENDED');
    }
    return findings;
  },
};

const parentChild: Rule = {
  id: 'dates.parent-child',
  name: 'Parent and child dates',
  description: `Compares "born"/"died" metadata across parent/child links: a child born before a parent (contradiction), to a parent younger than ${YOUNGEST_PLAUSIBLE_PARENT}, or more than a year after a parent's death (review).`,
  check(world) {
    const findings: Finding[] = [];
    const seen = new Set<string>();
    for (const link of world.links) {
      const s = step(link);
      if (!s || s.hierarchy !== 'ANCESTRY') continue;
      const pair = `${s.lower}>${s.higher}`;
      if (seen.has(pair)) continue;
      seen.add(pair);
      const child = world.entry(s.lower);
      const parent = world.entry(s.higher);
      if (!child || !parent) continue;
      const childBirth = dateOf(child.fields, 'BIRTH');
      const parentBirth = dateOf(parent.fields, 'BIRTH');
      const parentDeath = dateOf(parent.fields, 'DEATH');
      const ids = [child.id, parent.id];
      const key = `${child.id}>${parent.id}`;
      if (childBirth && parentBirth) {
        const gap = childBirth.year - parentBirth.year;
        const both = `(${child.title}: ${when('BIRTH', childBirth)}; ${parent.title}: ${when('BIRTH', parentBirth)})`;
        if (gap < 0) {
          findings.push({
            ruleId: this.id,
            severity: 'ERROR',
            title: 'Child born before parent',
            message: `Possible chronology inconsistency: ${child.title} is recorded as being born ${years(gap)} before ${parent.title}, who is recorded as ${child.title}'s parent ${both}.`,
            entryIds: ids,
            key: `${key}|before`,
          });
        } else if (gap < YOUNGEST_PLAUSIBLE_PARENT) {
          findings.push({ ruleId: this.id, severity: 'WARNING', title: 'Very young parent', message: `${parent.title} would have been ${years(gap)} old when ${child.title} was born ${both}.`, entryIds: ids, key: `${key}|young` });
        }
      }
      if (childBirth && parentDeath && childBirth.year - parentDeath.year > 1) {
        findings.push({
          ruleId: this.id,
          severity: 'WARNING',
          title: "Born after a parent's death",
          message: `${child.title} is recorded as being born ${years(childBirth.year - parentDeath.year)} after ${parent.title} ${when('DEATH', parentDeath)}.`,
          entryIds: ids,
          key: `${key}|posthumous`,
        });
      }
    }
    return findings;
  },
};

const eventOutsideLifetime: Rule = {
  id: 'dates.event-outside-lifetime',
  name: 'Event outside a lifetime',
  description:
    'A chronology event linked to a person or creature dated before their birth or after their death - or linked to a place, faction, or anything else dated before its founding or after its end.',
  check(world) {
    const findings: Finding[] = [];
    for (const event of world.events) {
      const year = world.year(event.start);
      if (year === null) continue;
      for (const entryId of event.entryIds) {
        const entry = world.entry(entryId);
        if (!entry) continue;
        const being = BEINGS.has(entry.entityType);
        const begins: DateFact = being ? 'BIRTH' : 'FOUNDED';
        const ends: DateFact = being ? 'DEATH' : 'ENDED';
        const eventText = `"${event.title}" (${displayDate(event.start)})`;
        const from = dateOf(entry.fields, begins);
        if (from && year < from.year) {
          findings.push({
            ruleId: this.id,
            severity: 'WARNING',
            title: this.name,
            message: `${entry.title} is linked to ${eventText}, ${years(from.year - year)} before ${entry.title}'s recorded ${DATE_FACTS[begins].noun} (${when(begins, from)}).`,
            entryIds: [entry.id],
            key: `${event.id}|${entry.id}|before`,
          });
        }
        const to = dateOf(entry.fields, ends);
        if (to && year > to.year) {
          findings.push({
            ruleId: this.id,
            severity: 'WARNING',
            title: this.name,
            message: `${entry.title} is linked to ${eventText}, ${years(year - to.year)} after ${entry.title}'s recorded ${DATE_FACTS[ends].noun} (${when(ends, to)})${being ? '. A ghost, a legend, or a date to fix?' : '.'}`,
            entryIds: [entry.id],
            key: `${event.id}|${entry.id}|after`,
          });
        }
      }
    }
    return findings;
  },
};

const eventOutsideEra: Rule = {
  id: 'chronology.event-outside-era',
  name: 'Event outside its era',
  description: 'A chronology event filed under an era whose start/end dates it falls outside of.',
  check(world) {
    const findings: Finding[] = [];
    for (const event of world.events) {
      const era = world.era(event.eraId);
      if (!era) continue;
      const at = event.start.sortKey;
      const where = at < era.start.sortKey ? `begins ${displayDate(era.start)}` : era.end && at > era.end.sortKey ? `ends ${displayDate(era.end)}` : null;
      if (where === null) continue;
      findings.push({
        ruleId: this.id,
        severity: 'ERROR',
        title: this.name,
        message: `"${event.title}" (${displayDate(event.start)}) is filed under the era "${era.name}", which ${where}. Move the event to the right era, or fix one of the dates.`,
        entryIds: [...event.entryIds],
        key: event.id,
      });
    }
    return findings;
  },
};

const endBeforeStart: Rule = {
  id: 'chronology.end-before-start',
  name: 'Ends before it starts',
  description: 'A chronology event or era whose end date comes before its start date.',
  check(world) {
    const findings: Finding[] = [];
    for (const event of world.events) {
      if (event.end && event.end.sortKey < event.start.sortKey) {
        findings.push({
          ruleId: this.id,
          severity: 'ERROR',
          title: this.name,
          message: `The event "${event.title}" ends (${displayDate(event.end)}) before it starts (${displayDate(event.start)}).`,
          entryIds: [...event.entryIds],
          key: `event|${event.id}`,
        });
      }
    }
    for (const era of world.world.eras) {
      if (era.end && era.end.sortKey < era.start.sortKey) {
        findings.push({ ruleId: this.id, severity: 'ERROR', title: this.name, message: `The era "${era.name}" ends (${displayDate(era.end)}) before it starts (${displayDate(era.start)}).`, entryIds: [], key: `era|${era.id}` });
      }
    }
    return findings;
  },
};

const populationContainment: Rule = {
  id: 'numbers.population-containment',
  name: "Population doesn't fit",
  description:
    'Compares "population" metadata along location links (located in / part of / contains): a place with more people than the place it\'s in, or places whose populations add up to more than their container\'s.',
  check(world) {
    const contents = new Map<string, Set<string>>();
    for (const link of world.links) {
      const s = step(link);
      if (!s || s.hierarchy !== 'CONTAINMENT' || s.lower === s.higher) continue;
      contents.set(s.higher, (contents.get(s.higher) ?? new Set()).add(s.lower));
    }
    const findings: Finding[] = [];
    for (const [containerId, inside] of contents) {
      const container = world.entry(containerId);
      const containerPop = container ? quantityOf(container.fields, 'POPULATION') : null;
      if (!container || containerPop === null) continue;
      let total = 0;
      const counted: string[] = [];
      let anySingleTooBig = false;
      for (const insideId of sortedIds(inside)) {
        const place = world.entry(insideId);
        const pop = place ? quantityOf(place.fields, 'POPULATION') : null;
        if (!place || pop === null) continue;
        total += pop;
        counted.push(insideId);
        if (pop > containerPop) {
          anySingleTooBig = true;
          findings.push({
            ruleId: this.id,
            severity: 'WARNING',
            title: this.name,
            message: `Population data may require review: ${place.title} has a recorded population of ${count(pop)}, more than ${container.title} (${count(containerPop)}), which it's located in.`,
            entryIds: [insideId, containerId],
            key: `${insideId}>${containerId}`,
          });
        }
      }
      if (!anySingleTooBig && counted.length >= 2 && total > containerPop) {
        const names = counted.map((id) => world.title(id)).join(', ');
        findings.push({
          ruleId: this.id,
          severity: 'WARNING',
          title: this.name,
          message: `Population data may require review: the places located in ${container.title} (${names}) add up to ${count(total)} people, more than ${container.title}'s recorded population of ${count(containerPop)}.`,
          entryIds: [containerId, ...counted],
          key: `sum|${containerId}`,
        });
      }
    }
    return findings;
  },
};

const foodSupply: Rule = {
  id: 'numbers.food-supply',
  name: 'Population vs. food production',
  description: `A place whose "food production" (how many people its own food feeds) falls well short of its "population" - below ${Math.round(FOOD_SHORTFALL_RATIO * 100)}%.`,
  check(world) {
    const findings: Finding[] = [];
    for (const entry of world.entries) {
      const population = quantityOf(entry.fields, 'POPULATION');
      const food = quantityOf(entry.fields, 'FOOD');
      if (population === null || food === null || population <= 0 || food >= population * FOOD_SHORTFALL_RATIO) continue;
      findings.push({
        ruleId: this.id,
        severity: 'WARNING',
        title: this.name,
        message:
          `Population/economic data may require review: ${entry.title} has a population of ${count(population)} but its food production feeds only ${count(food)} - ` +
          `a shortfall of ${count(population - food)}. If imports, magic, or famine explain it, dismiss this.`,
        entryIds: [entry.id],
        key: entry.id,
      });
    }
    return findings;
  },
};

const unreadableFacts: Rule = {
  id: 'facts.unreadable',
  name: 'Unreadable fact',
  description:
    "A metadata field the engine recognizes (born, died, founded, population, food production...) whose value isn't a year or number it can read - so the checks that use it were skipped.",
  check(world) {
    const findings: Finding[] = [];
    for (const entry of world.entries) {
      for (const problem of unreadable(entry.fields)) {
        findings.push({
          ruleId: this.id,
          severity: 'INFO',
          title: this.name,
          message: `Couldn't read ${entry.title}'s "${problem.key}" ("${problem.value}") - expected ${problem.expected}. Checks that use it were skipped.`,
          entryIds: [entry.id],
          key: `${entry.id}|${normalizeKey(problem.key)}`,
        });
      }
    }
    return findings;
  },
};

// ---- structure ----

const brokenLinks: Rule = {
  id: 'links.broken',
  name: 'Broken link',
  description: 'A relationship pointing at an entry that no longer exists.',
  check(world) {
    return world.links
      .filter((l) => !world.entry(l.targetEntryId))
      .map((l) => ({
        ruleId: this.id,
        severity: 'ERROR' as const,
        title: this.name,
        message: `${world.title(l.sourceEntryId)} has a "${normalize(l.relationshipType)}" link to an entry that no longer exists. Remove the link from ${world.title(l.sourceEntryId)}'s Relationships section.`,
        entryIds: [l.sourceEntryId],
        key: `${l.sourceEntryId}|${normalize(l.relationshipType)}|${l.targetEntryId}`,
      }));
  },
};

const duplicateTitles: Rule = {
  id: 'entries.duplicate-title',
  name: 'Duplicate entries',
  description: 'Two or more entries of the same type with the same title - usually the same thing entered twice.',
  check(world) {
    const groups = new Map<string, WorldEntry[]>();
    for (const entry of world.entries) {
      const key = `${typeName(entry).toLowerCase()}|${javaStrip(entry.title).toLowerCase()}`;
      groups.set(key, [...(groups.get(key) ?? []), entry]);
    }
    const findings: Finding[] = [];
    for (const [key, entries] of groups) {
      if (entries.length < 2) continue;
      const first = entries[0]!;
      findings.push({
        ruleId: this.id,
        severity: 'WARNING',
        title: this.name,
        message: `${entries.length} ${typeName(first)} entries are titled "${first.title}". If they're the same thing, merge them; if not, give one a more specific title.`,
        entryIds: entries.map((e) => e.id),
        key,
      });
    }
    return findings;
  },
};

const isolatedEntries: Rule = {
  id: 'entries.isolated',
  name: 'Isolated entry',
  description: 'An entry nothing connects to: no relationships in either direction, no chronology event, and no Atlas pin or region refers to it.',
  check(world) {
    const connected = new Set(world.world.entryIdsOnMaps);
    for (const link of world.links) {
      connected.add(link.sourceEntryId);
      connected.add(link.targetEntryId);
    }
    for (const event of world.events) for (const id of event.entryIds) connected.add(id);
    return world.entries
      .filter((entry) => !connected.has(entry.id))
      .map((entry) => ({
        ruleId: this.id,
        severity: 'INFO' as const,
        title: this.name,
        message: `${entry.title} (${typeName(entry)}) isn't connected to anything yet - no relationships, chronology events, or map pins refer to it. Linking it in makes it easier to find and lets the other checks see it.`,
        entryIds: [entry.id],
        key: entry.id,
      }));
  },
};

/** Every rule, in the desktop's order (`ConsistencyEngine.standard`). */
const RULES: readonly Rule[] = [
  conflicts, oneSided, cycles, selfLinks, unusualTypes, tooManyParents,
  startAfterEnd, parentChild, eventOutsideLifetime, eventOutsideEra, endBeforeStart, populationContainment, foodSupply, unreadableFacts,
  brokenLinks, duplicateTitles, isolatedEntries,
];

export const CONSISTENCY_RULES: readonly ConsistencyRule[] = RULES.map(({ id, name, description }) => ({ id, name, description }));

/** The keys each fact is read from, as "What's checked" lists them. */
export const FACT_KEYS = {
  Birth: DATE_FACTS.BIRTH.keys,
  Death: DATE_FACTS.DEATH.keys,
  Founding: DATE_FACTS.FOUNDED.keys,
  End: DATE_FACTS.ENDED.keys,
  Population: QUANTITY_FACTS.POPULATION.keys,
  'Food production (people it feeds)': QUANTITY_FACTS.FOOD.keys,
} as const;

/** Every rule over the world, most serious first, then by title and message. A rule that fails is skipped. */
export function checkWorld(world: World): Finding[] {
  const view = new WorldView(world);
  const findings: Finding[] = [];
  for (const rule of RULES) {
    try {
      findings.push(...rule.check(view));
    } catch (error) {
      console.warn(`Consistency rule ${rule.id} failed and was skipped`, error);
    }
  }
  return findings.sort((a, b) => SEVERITIES.indexOf(a.severity) - SEVERITIES.indexOf(b.severity) || javaCompare(a.title, b.title) || javaCompare(a.message, b.message));
}

// ---- the realm's world (ConsistencyService.worldModel) ----

const DATE_COLUMNS = `CAST(start_sort_key AS TEXT) AS start_key, start_label, start_uncertain, has_end, CAST(end_sort_key AS TEXT) AS end_key, end_label, end_uncertain`;

function datesOf(row: SqlRow): { start: ChronologyDate; end: ChronologyDate | null } {
  return {
    start: { sortKey: BigInt(text(row['start_key']) || '0'), label: text(row['start_label']), uncertain: int(row['start_uncertain']) !== 0 },
    end: int(row['has_end']) !== 0 ? { sortKey: BigInt(text(row['end_key']) || '0'), label: text(row['end_label']), uncertain: int(row['end_uncertain']) !== 0 } : null,
  };
}

/** The realm as the desktop's check reads it, with the same queries, so the same order. */
export function realmWorld(conn: SqlConnection): World {
  const entries = conn.all('SELECT id, entity_type, custom_type_name, title, metadata FROM lore_entries ORDER BY title COLLATE NOCASE').map((row) => ({
    id: text(row['id']),
    entityType: text(row['entity_type']),
    customTypeName: text(row['custom_type_name']),
    title: text(row['title']),
    fields: [...parseJavaProperties(text(row['metadata'])).entries()],
  }));
  const links = conn
    .all('SELECT source_entry_id, target_entry_id, relationship_type FROM entry_links')
    .map((row) => ({ sourceEntryId: text(row['source_entry_id']), targetEntryId: text(row['target_entry_id']), relationshipType: text(row['relationship_type']) }));
  const events = conn.all(`SELECT id, era_id, title, ${DATE_COLUMNS} FROM chronology_events ORDER BY start_sort_key`).map((row) => {
    const id = text(row['id']);
    const era = row['era_id'];
    return {
      id,
      eraId: era === null || era === undefined ? null : text(era),
      title: text(row['title']),
      ...datesOf(row),
      entryIds: conn.all('SELECT entry_id FROM chronology_event_entries WHERE event_id = ?', [id]).map((r) => text(r['entry_id'])),
    };
  });
  const eras = conn.all(`SELECT id, name, ${DATE_COLUMNS} FROM chronology_eras ORDER BY start_sort_key`).map((row) => ({ id: text(row['id']), name: text(row['name']), ...datesOf(row) }));
  const onMaps = new Set(
    conn
      .all('SELECT linked_entry_id FROM map_markers WHERE linked_entry_id IS NOT NULL UNION SELECT linked_entry_id FROM map_regions WHERE linked_entry_id IS NOT NULL')
      .map((row) => text(row['linked_entry_id'])),
  );
  let calendar: CalendarSystem | null = null;
  try {
    calendar = activeCalendar(conn);
  } catch (error) {
    if (!(error instanceof CalendarError)) throw error;
  }
  return { entries, links, events, eras, entryIdsOnMaps: onMaps, calendar };
}

// ---- the report, and dismissals (ConsistencyService, ConsistencyRepository) ----

export interface DismissedFinding {
  readonly finding: Finding;
  readonly note: string;
  readonly dismissedAt: string;
}

export interface ConsistencyReport {
  readonly active: readonly Finding[];
  readonly dismissed: readonly DismissedFinding[];
  readonly checkedAt: string;
}

/** Checks the whole realm, and splits the findings into active and dismissed. */
export function checkRealm(conn: SqlConnection, now: string = new Date().toISOString()): ConsistencyReport {
  const findings = checkWorld(realmWorld(conn));
  const dismissals = new Map<string, { note: string; dismissedAt: string }>();
  for (const row of conn.all('SELECT fingerprint, note, dismissed_at FROM consistency_dismissals ORDER BY dismissed_at DESC')) {
    const key = text(row['fingerprint']);
    if (!dismissals.has(key)) dismissals.set(key, { note: text(row['note']), dismissedAt: text(row['dismissed_at']) });
  }
  const active: Finding[] = [];
  const dismissed: DismissedFinding[] = [];
  for (const finding of findings) {
    const dismissal = dismissals.get(fingerprint(finding));
    if (dismissal) dismissed.push({ finding, ...dismissal });
    else active.push(finding);
  }
  return { active, dismissed, checkedAt: now };
}

export const DISMISS_NOTE_MAX = 1000;

/** Marks a finding as intentional, with a note, so it isn't reported until it's restored. */
export function dismissFinding(conn: SqlConnection, finding: Pick<Finding, 'ruleId' | 'key' | 'message'>, note: string, now: string = new Date().toISOString()): void {
  if (note.length > DISMISS_NOTE_MAX) throw new EntryEditError('invalid', `A note can be at most ${DISMISS_NOTE_MAX.toLocaleString('en')} characters.`);
  if (!CONSISTENCY_RULES.some((rule) => rule.id === finding.ruleId)) throw new EntryEditError('invalid', 'That finding could not be dismissed.');
  conn.run(
    `INSERT INTO consistency_dismissals (fingerprint, rule_id, summary, note, dismissed_at) VALUES (?, ?, ?, ?, ?)
     ON CONFLICT(fingerprint) DO UPDATE SET summary = excluded.summary, note = excluded.note, dismissed_at = excluded.dismissed_at`,
    [fingerprint(finding as Finding), finding.ruleId, finding.message, javaStrip(note), now],
  );
}

export function restoreFinding(conn: SqlConnection, findingFingerprint: string): void {
  conn.run('DELETE FROM consistency_dismissals WHERE fingerprint = ?', [findingFingerprint]);
}
